import {
  computed, observable, makeObservable, action, makeAutoObservable
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { navigate } from 'gatsby';
import { ChangeSectionCategory, CPDTagOption, PAGE_SIZE, VmList } from '../constants/options';
import { SetLocalStorage } from '../utilities/cryptography';
import { APPROVAL_NOTES_LIST, APPROVAL_NOTES_LIST_EXPIRY, CHANGE_SECTION_CATEGORY_OPTIONS, COMPLIANCE_DOCUMENT_CATEGORY_OPTIONS, CONTACT_TYPE_OPTIONS, CPD_PROVIDER_OPTIONS, EVENT_TAG_OPTIONS, HEALTH_FUND_OPTIONS, INSURANCE_PROVIDER_OPTIONS, MEMBER_TYPE_OPTIONS, MEMBERSHIP_LEVEL_OPTIONS, MEMBERSHIP_STATUS_OPTIONS, MODALITY_OPTIONS, NOTIFICATION_CATEGORIES, PAYMENT_CATEGORY_OPTIONS, PHOTO_ID_DOCUMENT_CATEGORY_OPTIONS, PUBLIC_DOCUMENT_CATEGORY, QUALIFICATION_ATTACHMENT_CATEGORY_OPTIONS, QUALIFICATION_TYPES, SERVICE_CATEGORY_OPTIONS, USER_INFO } from '../constants/storage-keys';
import { ConvertListToCommonOptions } from '../utilities/general';
import moment from 'moment';
import { isDevelopment } from '../constants/settings';

export default class OptionStore {

  loading: boolean = false;
  optionLoading: boolean = false;
  loadingApprovalNotes: boolean = false;

  savingOption: boolean = false;
  deletingOption: boolean = false;

  serviceCategories: VmList = { totalCount: 0, items: [] };
  serviceCategoryInfo: any = null;

  announcementTypes: VmList = { totalCount: 0, items: [] };
  announcementTypeInfo: any = null;

  insuranceProviderList: VmList = { totalCount: 0, items: [] };
  insuranceProviderInfo: any = null;

  paymentCategoryList: VmList = { totalCount: 0, items: [] };
  paymentCategoryInfo: any = null;

  complianceDocumentCategoryList: VmList = { totalCount: 0, items: [] };
  complianceDocumentCategoryInfo: any = null;

  modalityList: VmList = { totalCount: 0, items: [] };
  modalityInfo: any = null;

  addingPublicDownloadCategory: boolean = false;
  updatingPublicDownloadCategory: boolean = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.rootStore = rootStore;
  }

  createAnnouncementCategory = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Announcement/Category`, reqModel);
      this.announcementTypeInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  getAnnouncementCategory = async (id: number) => {
    this.optionLoading = true;
    try {
      const res = await Axios.get(`/api/Announcement/Category/${id}`);
      this.announcementTypeInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  updateAnnouncementCategory = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.put(`/api/Announcement/Category`, reqModel);
      this.announcementTypeInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }

  };
  deleteAnnouncementCategory = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Announcement/Category/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };



  // === CMASA ===

  getServiceCategories = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Service/Category/Search/0/0`, reqModel);

      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.value.localeCompare(b.value));

      SetLocalStorage(SERVICE_CATEGORY_OPTIONS, sortedList);
      this.serviceCategories = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };


  getInsuranceProviderList = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Provider/Category/Search/0/0`, reqModel);

      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      this.insuranceProviderList = res.data;
      SetLocalStorage(INSURANCE_PROVIDER_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };


  getModalitiesList = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Modality/Category/Search/0/0`, reqModel);

      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.value.localeCompare(b.value));

      this.modalityList = res.data;
      SetLocalStorage(MODALITY_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  getPaymentCategoryList = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Payment/Category/Search/0/0`, reqModel);

      const sortedList = res.data.items
        .map(item => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));

      this.paymentCategoryList = res.data;
      SetLocalStorage(PAYMENT_CATEGORY_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  getComplianceDocumentCategory = async (reqModal: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Compliance/Document/Category/Search/0/0`, reqModal);

      const sortedList = res.data.items
        .map((item: { id: number, name: string, description: string | null }) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      this.complianceDocumentCategoryList = res.data;
      SetLocalStorage(COMPLIANCE_DOCUMENT_CATEGORY_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  getContactCategoryList = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Contact/Category/Search/0/0`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };


  searchMembershipStatus = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Contact/Membership/Status/Search/0/0`, reqModel);

      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.name));

      SetLocalStorage(MEMBERSHIP_STATUS_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchContactType = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Contact/Category/Search/0/0`, reqModel);

      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(CONTACT_TYPE_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };


  searchPhotoIdDocumentCategory = async (reqModal: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/PhotoId/Category/Search/0/0`, reqModal);

      const sortedList = res.data.items
        .map((item: { id: number, name: string, description: string | null }) => ({
          ...item,
          id: item.id,
          value: item.id,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(PHOTO_ID_DOCUMENT_CATEGORY_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };


  searchChangeCategoryOptions = async (reqModal: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Change/Category/Search/0/0`, reqModal);

      const sortedList = res.data.items
        .map((item: { id: number, name: string, description: string | null }) => ({
          ...item,
          id: item.id,
          value: item.id,
          label: item.name,
        }))
      // .sort((a: any, b: any) => a.name.localeCompare(b.name));

      SetLocalStorage(CHANGE_SECTION_CATEGORY_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  searchMembershipLevel = async (reqModal: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Contact/Membership/Status/Search/0/0`, reqModal);

      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(MEMBERSHIP_LEVEL_OPTIONS, sortedList);
      return Promise.resolve(sortedList);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  searchQualificationAttachmentCategory = async (reqModal: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Qualification/Attachment/Type/0/0`, reqModal);

      const sortedList = res.data.items.map((item: any) => ({
        ...item,
        id: item.id,
        value: item.id,
        label: item.name,
      })).sort((a: any, b: any) => a.label.localeCompare(b.name));


      SetLocalStorage(QUALIFICATION_ATTACHMENT_CATEGORY_OPTIONS, sortedList);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  searchHealthFundCategories = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Provider/Category/Search/0/0`, reqModel);
      const sortedList = res.data.items
        .map((item: { id: number, name: string, description: string | null }) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(HEALTH_FUND_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  searchCPDProviders = async (reqModel: {
    searchTerm?: string
  }) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Cpd/Organization/Search/0/0`, reqModel);
      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(CPD_PROVIDER_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  addCPDProvider = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Cpd/Organization`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  }

  updateCPDProvider = async (reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.put(`/api/Cpd/Organization`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  }

  deleteCPDProvider = async (id: number) => {
    this.optionLoading = true;
    try {
      const res = await Axios.delete(`/api/Cpd/Organization/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  }


  getMemberTypeOptions = async () => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Contact/Title/Search/0/0`, {});
      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.id,
          label: item.name,
        }))
      // .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(MEMBER_TYPE_OPTIONS, sortedList);
      return Promise.resolve(sortedList);

    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  searchPublicDownloadCategories = async (reqModal: {
    chName?: string,
    enName?: string
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Download/Category/Search/0/0`, reqModal);

      const sortedList = res.data.items.map((item: any) => ({
        ...item,
        id: item.id,
        value: item.id,
        label: item.enName,
      }));

      SetLocalStorage(PUBLIC_DOCUMENT_CATEGORY, sortedList);
      return Promise.resolve(sortedList);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  createPublicDownloadCategory = async (reqModel: {
    chName: string,
    enName: string,
    description: string
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Download/Category`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  deletePublicDownloadCategory = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Download/Category/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  updatePublicDownloadCategory = async (reqModel: {
    id: number,
    chName: string,
    enName: string
  }) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Download/Category`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  getQualificationTypes = async () => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Qualification/Category/Search/0/0`, {});
      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.id,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(QUALIFICATION_TYPES, sortedList);
      return Promise.resolve(sortedList);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  }

  // configure approval notes

  getApprovalNotesList = async (reqModal: {
    categoryId?: ChangeSectionCategory | null,
    reason?: string,
    isDeleted?: boolean,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string
  } = {}, skip: number = 0, take: number = 0) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Reason/Search/${skip}/${take}`, reqModal);
      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.label,
        }))

      if (skip === 0 && take === 0 && Object.keys(reqModal).length === 0) {
        SetLocalStorage(APPROVAL_NOTES_LIST, sortedList);
        SetLocalStorage(APPROVAL_NOTES_LIST_EXPIRY, moment().add(1, 'minute').toISOString());
      }

      return Promise.resolve({ ...res.data, items: sortedList });
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  }

  addApprovalNote = async (reqModel: {
    name: string,
    description: string
  }) => {
    this.loadingApprovalNotes = true;
    try {
      const res = await Axios.post(`/api/Member/Reason`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingApprovalNotes = false;
    }
  }

  updateApprovalNote = async (reqModel: {
    id: number,
    name: string,
    description: string
  }) => {
    this.loadingApprovalNotes = true;
    try {
      const res = await Axios.put(`/api/Member/Reason`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingApprovalNotes = false;
    }
  }

  deleteApprovalNote = async (id: number) => {
    this.loadingApprovalNotes = true;
    try {
      const res = await Axios.delete(`/api/Member/Reason/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingApprovalNotes = false;
    }
  }


  // ============ Notification Categories ============

  getNotificationCategories = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Notification/Category/Search/0/0`, reqModel);
      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          value: item.name,
          label: item.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      SetLocalStorage(NOTIFICATION_CATEGORIES, sortedList);
      return Promise.resolve(sortedList);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // ============ event tags ============

  searchEventTagList = async (skip: number, take: number, reqModel: any) => {
    this.optionLoading = true;
    try {
      const res = await Axios.post(`/api/Cpd/Tag/Search/${skip}/${take}`, reqModel);
      const sortedList = res.data.items
        .map((item: any) => ({
          ...item,
          id: item.id,
          label: item.enName,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
      SetLocalStorage(EVENT_TAG_OPTIONS, sortedList);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.optionLoading = false;
    }
  };

  createEventTagOption = async (reqModel: CPDTagOption) => {
    this.savingOption = true;
    try {
      const res = await Axios.post(`/api/Cpd/Tag`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingOption = false;
    }
  };

  updateEventTagOption = async (reqModel: CPDTagOption) => {
    this.savingOption = true;
    try {
      const res = await Axios.put(`/api/Cpd/Tag`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingOption = false;
    }
  };

  deleteEventTagOption = async (id: number) => {
    this.deletingOption = true;
    try {
      const res = await Axios.delete(`/api/Cpd/Tag/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingOption = false;
    }
  };

  optionsFetchList = [
    this.getComplianceDocumentCategory,
    this.searchMembershipStatus,
    this.getInsuranceProviderList,
    this.getModalitiesList,
    this.getPaymentCategoryList,
    this.getServiceCategories,
    this.getContactCategoryList,
    this.searchContactType,
    this.searchPhotoIdDocumentCategory,
    this.searchChangeCategoryOptions,
    this.searchMembershipLevel,
    this.searchQualificationAttachmentCategory,
    this.searchHealthFundCategories,
    this.searchCPDProviders,
    this.getMemberTypeOptions,
    this.searchPublicDownloadCategories,
    this.getQualificationTypes,
    this.getApprovalNotesList,
    this.getNotificationCategories,
  ];
}